import React, { useState, useEffect } from 'react';

export function ProUpgradePromotion({ user, onUpgrade, onDismiss }) {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (user?.subscription_plan === 'free' && !dismissed) {
      const lastDismissed = localStorage.getItem('pro_promo_dismissed');
      const lastDismissedTime = lastDismissed ? parseInt(lastDismissed) : 0;
      const hoursSinceDismissed = (Date.now() - lastDismissedTime) / (1000 * 60 * 60);
      
      if (hoursSinceDismissed > 24) {
        const timer = setTimeout(() => setShow(true), 30000);
        return () => clearTimeout(timer);
      }
    }
  }, [user, dismissed]);

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    localStorage.setItem('pro_promo_dismissed', Date.now().toString());
    if (onDismiss) onDismiss();
  };

  if (!show || user?.subscription_plan !== 'free') return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
      <div className="glass-card rounded-2xl p-6 md:p-8 max-w-md w-full border border-primary/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-purple-500/10 pointer-events-none" />
        
        <div className="relative">
          <button 
            onClick={handleDismiss}
            className="absolute -top-2 -right-2 text-gray-400 hover:text-white text-xl p-2"
          >
            ✕
          </button>
          
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-primary to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/30">
              <span className="text-4xl">⭐</span>
            </div>
            <h2 className="text-2xl font-bold text-white font-['Outfit'] mb-2">
              OFERTA ESPECIAL!
            </h2>
            <p className="text-muted-foreground">
              Só para novos usuários como você
            </p>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
              <span className="text-green-400 text-xl">✓</span>
              <span className="text-white text-sm"><strong>Clientes ilimitados</strong> (você tem apenas 5)</span>
            </div>
            <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
              <span className="text-green-400 text-xl">✓</span>
              <span className="text-white text-sm"><strong>Orçamentos ilimitados</strong> (você tem apenas 5)</span>
            </div>
            <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
              <span className="text-green-400 text-xl">✓</span>
              <span className="text-white text-sm"><strong>Materiais ilimitados</strong> (você tem apenas 10)</span>
            </div>
            <div className="flex items-center gap-3 bg-primary/10 border border-primary/20 rounded-lg p-3">
              <span className="text-primary text-xl">🚀</span>
              <span className="text-white text-sm">Suporte prioritário e atualizações</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-500/20 to-primary/20 border-2 border-primary rounded-xl p-4 mb-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">-58% OFF</span>
              <span className="text-red-400 line-through text-lg">R$ 1.200/ano</span>
            </div>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-4xl font-bold text-primary">R$ 49,90</span>
              <span className="text-muted-foreground">/mês</span>
            </div>
            <p className="text-sm text-white mt-2">
              <strong>10x R$ 49,90</strong> = R$ 499/ano
            </p>
            <p className="text-xs text-green-400 mt-1 font-semibold">
              Economize R$ 701 por ano!
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => { handleDismiss(); onUpgrade(); }}
              className="w-full btn-primary py-4 rounded-xl text-lg font-semibold animate-pulse-glow flex items-center justify-center gap-2"
            >
              ⭐ QUERO ESSA OFERTA!
            </button>
            <button
              onClick={handleDismiss}
              className="w-full text-muted-foreground hover:text-white font-medium transition-colors py-2"
            >
              Continuar com Plano Gratuito
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProBanner({ user, onUpgrade }) {
  const [dismissed, setDismissed] = useState(false);
  
  if (user?.subscription_plan !== 'free' || dismissed) return null;
  
  const lastDismissed = localStorage.getItem('pro_banner_dismissed');
  if (lastDismissed && (Date.now() - parseInt(lastDismissed)) < 3600000) return null;
  
  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pro_banner_dismissed', Date.now().toString());
  };
  
  return (
    <div
      className="bg-gradient-to-r from-primary/90 via-amber-500/90 to-orange-500/90 px-4 py-2 text-center relative animate-fade-in"
      style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 0.5rem)' }}
      data-testid="pro-banner"
    >
      <button 
        onClick={handleDismiss}
        className="absolute right-2 text-black/50 hover:text-black p-1"
        style={{ top: 'calc(50% + env(safe-area-inset-top, 0px) / 2)', transform: 'translateY(-50%)' }}
        aria-label="Fechar banner"
      >
        ✕
      </button>
      <p className="text-black font-medium text-sm pr-6">
        🔥 <strong>PROMOÇÃO:</strong> Plano Pro com <strong>58% OFF</strong> - De R$1.200 por apenas <strong>10x R$49,90</strong>!{' '}
        <button 
          onClick={onUpgrade}
          className="underline font-bold hover:no-underline"
        >
          APROVEITAR AGORA →
        </button>
      </p>
    </div>
  );
}

export function ProUpgradeCard({ user, onUpgrade }) {
  if (user?.subscription_plan !== 'free') return null;
  
  return (
    <div 
      className="glass-card rounded-xl overflow-hidden border-2 border-primary/50 cursor-pointer hover:border-primary transition-all group"
      onClick={onUpgrade}
      data-testid="pro-upgrade-card"
    >
      <div className="bg-gradient-to-r from-primary via-amber-500 to-orange-500 p-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
        <div className="flex items-center justify-between relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-black/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <span className="text-2xl">⭐</span>
            </div>
            <div>
              <h3 className="font-bold text-black text-lg">Seja PRO!</h3>
              <p className="text-black/70 text-sm">Desbloqueie todo o potencial</p>
            </div>
          </div>
          <div className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded animate-pulse">
            -58% OFF
          </div>
        </div>
      </div>
      
      <div className="p-4 space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-green-400">✓</span>
          <span className="text-white">Clientes <strong>ilimitados</strong></span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-green-400">✓</span>
          <span className="text-white">Orçamentos <strong>ilimitados</strong></span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-green-400">✓</span>
          <span className="text-white">Materiais <strong>ilimitados</strong></span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-green-400">✓</span>
          <span className="text-white">Suporte <strong>prioritário</strong></span>
        </div>
      </div>
      
      <div className="p-4 pt-0">
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-red-400 line-through text-sm">R$ 100/mês</span>
          <span className="text-3xl font-bold text-primary">10x R$ 49,90</span>
        </div>
        <button className="w-full btn-primary py-3 rounded-lg font-semibold text-base group-hover:animate-pulse-glow flex items-center justify-center gap-2">
          ⭐ QUERO SER PRO!
        </button>
      </div>
    </div>
  );
}

export function ProFloatingBadge({ user, onUpgrade }) {
  const [show, setShow] = useState(false);
  
  useEffect(() => {
    if (user?.subscription_plan === 'free') {
      const timer = setTimeout(() => setShow(true), 10000);
      return () => clearTimeout(timer);
    }
  }, [user]);
  
  if (!show || user?.subscription_plan !== 'free') return null;
  
  return (
    <button
      onClick={onUpgrade}
      className="hidden md:flex fixed bottom-8 left-4 z-40 bg-gradient-to-r from-primary to-amber-500 text-black px-4 py-2 rounded-full font-bold text-sm shadow-lg shadow-primary/30 hover:scale-105 transition-transform animate-bounce-slow items-center gap-2"
      data-testid="pro-floating-badge"
    >
      <span>⭐</span>
      <span>SEJA PRO -58%</span>
    </button>
  );
}

export default ProUpgradePromotion;
